import { countries_length, languages } from '../config/config';
import { COLORS } from '../constants/constants';
import { setToastData } from '../store/slices/commonSlice';
import { setUserData } from '../store/slices/contentSlice';
import store from '../store/store';
import { setToken, showToast } from './utils';
import { logoutAction } from '../store/actions/authActions';

// regex patterns
export const alphabet_regix = /^[A-Za-z\u0600-\u06FF\s]*$/;
export const alphabet_dash_regix = /^[A-Za-z\u0600-\u06FF\s-]+$/;
export const numeric_integer_regix = /^\d*$/;
export const emailReg = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/; // check
export const arabic_text_regix = /^[\u0600-\u06FF\s]*$/;
export const alphabet_numeric_regix = /^[A-Za-z0-9\u0600-\u06FF]*$/;
export const numeric_decimal_regix = /^\d*(\.\d*)?$/;
export const iban_ksa_regix = /^SA\d{22}$/;
export const iban_regix = /^(?!SA)(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{15,34}$/;
export const swift_code_regix = /^[A-Za-z\u0600-\u06FF]{6}[A-Za-z0-9\u0600-\u06FF]{2}(?:[A-Za-z0-9\u0600-\u06FF]{3})?$/;

export function requestHandler({
  apiService,
  setData,
  setLoading,
  isSuccessMessage = false,
  param_data,
  onDone,
  onError,
  isErrorMessage = true,
  isModal = false,
  isBinary = false,
}) {
  return async (dispatch) => {
    const content = store.getState().content;
    const language = content?.language || 'English';
    const common = content?.dashboardContent;
    const errorText = language === languages.arb ? common?.something_went_wrong_arabic : common?.something_went_wrong;

    try {
      setLoading && dispatch(setLoading(true));
      const response = await apiService(param_data);

      if (isBinary) {
        onDone && onDone(response);
      } else if (response?.status) {
        setData && dispatch(setData(response?.result));

        if (isSuccessMessage && !response?.result?.isForcedLogin) {
          showToast({
            icon: 'success',
            title: response?.message ?? response?.result?.responseMessage ?? '',
          });
        }

        onDone && onDone(response);
      } else {
        if (isModal) {
          dispatch(
            setToastData({
              show_toast_modal: isErrorMessage,
              toast_description: response?.message ?? errorText,
              is_error: true,
              error_status: response?.status,
            }),
          );
        } else {
          showToast({
            icon: 'error',
            title: response?.message ?? errorText,
          });
        }
      }
    } catch (error) {
      onError && onError(error);

      const message = error?.data?.message ?? error?.data?.Message ?? errorText;

      if (error?.status !== 401 && isErrorMessage) {
        if (isModal || error?.data?.isModal) {
          dispatch(
            setToastData({
              show_toast_modal: isErrorMessage,
              toast_description: message,
              is_error: true,
              error_status: error?.status,
            }),
          );
        } else {
          showToast({
            icon: 'error',
            title: message,
          });
        }
      }

      if (error?.status === 401) {
        dispatch(setUserData(null));
        setToken("");
        dispatch(logoutAction());
        window.location.href = `${process.env.REACT_APP_OPEN_ACCOUNT_LINK}${language === languages.arb ? 'ar' : 'en'}`;
      }
    } finally {
      setLoading && dispatch(setLoading(false));
    }
  };
}

export function downloadBase64File({ base64Data, fileName, type }) {
  try {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Strip an existing data URI prefix so base64Data is always raw base64
    const prefixMatch = base64Data?.match(/^data:([^;,]+)?;base64,/i);
    if (prefixMatch) {
      type = type || prefixMatch[1];
      base64Data = base64Data.slice(prefixMatch[0].length);
    }

    const linkSource = type ? `data:${type};base64,${base64Data}` : `data:application/pdf;base64,${base64Data}`;

    if (isIOS) {
      const byteCharacters = atob(base64Data);
      const byteNumbers = [];
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers.push(byteCharacters.charCodeAt(i));
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: type || "application/pdf",
      });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      const mimeExtensions = {
        "application/pdf": "pdf",
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/gif": "gif",
        "image/webp": "webp",
        "text/plain": "txt",
        "text/csv": "csv",
        "application/msword": "doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
        "application/vnd.ms-excel": "xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
      };
      const extension = mimeExtensions[(type || "application/pdf").toLowerCase()] || "pdf";
      const baseName = fileName || "document";
      link.download = baseName.toLowerCase().endsWith(`.${extension}`) ? baseName : `${baseName}.${extension}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // Desktop and Android
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = fileName;

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  } catch (error) {
    alert('Error downloading file. Please try again. ' + error);
  }
}

export const iconColor = (svg, color) => {
  svg?.querySelectorAll('*').forEach((el) => {
    el?.setAttribute('stroke', color ?? COLORS.dark_grey);
  });
};

export const iconFillColor = (svg, color) => {
  svg?.querySelectorAll('*').forEach((el) => {
    el?.setAttribute('fill', color ?? COLORS.dark_grey);
  });
};

export const splitPhoneNumberAndCode = (value) => {
  let digits = String(value || '').replace(/\D/g, '');

  if (!digits) {
    return {
      number: '',
      code: '',
      country: null,
    };
  }

  // Remove international access prefixes (00, 000...)
  digits = digits.replace(/^0+/, '');

  const matchedCountry = countries_length
    .filter((country) => {
      const dialCode = String(country.dial_code);

      return digits.startsWith(dialCode);
    })
    .sort((a, b) => b.dial_code.length - a.dial_code.length)[0];

  if (!matchedCountry) {
    return {
      number: digits,
      code: '',
      country: null,
    };
  }

  return {
    number: digits.slice(matchedCountry.dial_code.length),
    code: matchedCountry.dial_code,
    country: { ...matchedCountry, dial_code: matchedCountry.dial_code?.startsWith('+') ? matchedCountry.dial_code : `+${matchedCountry.dial_code}` },
  };
};
export const getRejectedFieldData = (rejectedFields, field_codes, fieldName) => {
  return rejectedFields?.find(item => item?.fieldCode === field_codes?.[fieldName]?.fieldCode) || null;
}

export const getSectionRejectedFields = (rejection_summary, sectionId) => {
  const section = rejection_summary?.find(item => item?.sectionId === sectionId);
  
  const signatoryRejectedFields = section?.rejectedAuthorizedSignatories?.flatMap(signatory => signatory?.rejectedFields || []) || [];
  return [...(section?.rejectedFields || []), ...signatoryRejectedFields];
}

export const isExistingPortfolioChange = (values, data) => {
  return Number(values?.existingPublicEquity) !== Number(data?.existingPublicEquity) ||
    Number(values?.existingPrivateEquity) !== Number(data?.existingPrivateEquity) ||
    Number(values?.existingRealEstate) !== Number(data?.existingRealEstate) ||
    Number(values?.existingPrivateCredit) !== Number(data?.existingPrivateCredit) ||
    Number(values?.existingFixedIncomeSukuk) !== Number(data?.existingFixedIncomeSukuk) ||
    Number(values?.existingMoneyMarketMurabaha) !== Number(data?.existingMoneyMarketMurabaha);
}

export const isTargetPortfolioChange = (values, data) => {
  return Number(values?.targetPublicEquity) !== Number(data?.targetPublicEquity) ||
    Number(values?.targetPrivateEquity) !== Number(data?.targetPrivateEquity) ||
    Number(values?.targetRealEstate) !== Number(data?.targetRealEstate) ||
    Number(values?.targetPrivateCredit) !== Number(data?.targetPrivateCredit) ||
    Number(values?.targetFixedIncomeSukuk) !== Number(data?.targetFixedIncomeSukuk) ||
    Number(values?.targetMoneyMarketMurabaha) !== Number(data?.targetMoneyMarketMurabaha);
}

export const isBankValuesExist = (ksaBank, internationalBank, bankValues, getContent) => {
  return (dispatch) => {
    const isIbanExist = ksaBank?.find(bank => bank?.iban === bankValues?.iban) || internationalBank?.find(bank => bank?.iban === bankValues?.iban)
    const isAbaRoutingExist = internationalBank?.find(bank => bank?.abaRoutingNumber === bankValues?.abaRoutingNumber)
    const isAccountNumberExist = internationalBank?.find(bank => bank?.accountNumber === bankValues?.accountNumber)
    const isSwiftCodeExist = internationalBank?.find(bank => bank?.swiftCode === bankValues?.swiftCode)

    if (isIbanExist) {
      showToast({
        icon: 'error',
        title: getContent('iban_already_exist'),
      });
      return true;
    } else if (isAbaRoutingExist) {
      showToast({
        icon: 'error',
        title: getContent('aba_routing_already_exist'),
      })
      return true;
    } else if (isAccountNumberExist) {
      showToast({
        icon: 'error',
        title: getContent('account_number_already_exist'),
      })
      return true;
    } else if (isSwiftCodeExist) {
      showToast({
        icon: 'error',
        title: getContent('swift_code_already_exist'),
      })
      return true;
    }
    return false;
  }
}
// true when a labelled required field has no value (used to flag it after its form was saved)
export const isEmptyValue = (value) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0);

export const isRequiredEmpty = (isRequired, label, value) => Boolean(isRequired && label && isEmptyValue(value));
